import { Typography } from '@mui/material';
import { TattooChoices } from '@prisma/client';
import { getDay, isSameDay, parseISO } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import { Appointment, EmployeeToBusiness } from '../../interfaces';
import {
  convertDateToOnschedHours,
  convertIndexToShortDay,
  convertOnschedHoursToDateFromOffset,
  formatDollarAmount,
  getCustomerNameFromAppointment,
} from '../../utils/global/helper';
import { CalendarEvent, Resource } from '../../types/calendar';
import {
  EmployeesResourceMap,
  GetBatchTimeBlocksResponse,
  GetBatchTripsResponse,
} from '../../types/employees';
import {
  getAllBlockedDaysFromTimeBlock,
  getAllDaysFromTrip,
  getTimeBlockTimes,
} from '../employees';
import { OnSchedCommonType } from '../../types/on-sched';
import { DEFAULT_TIMEZONE } from '../../constants/appointments';

export const getAppointmentDisplayText = (appointment: Appointment) => {
  const {
    tattooLocation,
    tattooSize,
    priceType,
    minPrice,
    maxPrice,
    price,
    hourlyPrice,
  } = appointment;

  let priceText = '';
  if (priceType === 'HOURLY') {
    priceText = `${formatDollarAmount(hourlyPrice!)} per hour.`;
  }
  if (priceType === 'FIXED') {
    priceText = `${formatDollarAmount(price!)} for appointment.`;
  }
  if (priceType === 'RANGE') {
    priceText = `Price range of ${formatDollarAmount(
      minPrice!,
    )} - ${formatDollarAmount(maxPrice!)}.`;
  }

  let detailsText = '';
  if (!tattooLocation && !tattooSize) {
    detailsText = 'No details added for appointment.';
  } else if (tattooLocation && !tattooSize) {
    detailsText = `${tattooLocation} tattoo.`;
  } else if (!tattooLocation && tattooSize) {
    detailsText = `${tattooSize} tattoo.`;
  } else {
    detailsText = `${tattooSize} tattoo on clients ${tattooLocation}.`;
  }

  return `${detailsText} ${priceText}`;
};

export const getAppointmentDetailsComponent = (
  appointment: Appointment,
  sizeOptions: TattooChoices[] | undefined,
) => {
  const {
    tattooLocation,
    tattooSize,
    priceType,
    minPrice,
    maxPrice,
    price,
    hourlyPrice,
    serviceCategory,
    tattooDetails,
  } = appointment;

  let priceText = '';
  if (priceType === 'HOURLY') {
    priceText = `${formatDollarAmount(hourlyPrice!)} per hour.`;
  }
  if (priceType === 'FIXED') {
    priceText = `${formatDollarAmount(price!)} quote.`;
  }
  if (priceType === 'RANGE') {
    priceText = `${formatDollarAmount(minPrice!)} - ${formatDollarAmount(
      maxPrice!,
    )} quote.`;
  }

  let detailsText = '';
  const sizeItem = sizeOptions?.find(option => option.value === tattooSize);
  if (!tattooLocation && !tattooSize) {
    detailsText = 'No details added for appointment.';
  } else if (tattooLocation && !tattooSize) {
    detailsText = `${tattooLocation} tattoo.`;
  } else if (!tattooLocation && tattooSize) {
    let sizeText = tattooSize;
    if (sizeItem) {
      sizeText = sizeItem.name;
    }

    detailsText = `${sizeText} tattoo.`;
  } else {
    let sizeText = tattooSize;
    if (sizeItem) {
      sizeText = sizeItem.name;
    }
    detailsText = `${sizeText} tattoo on ${tattooLocation}.`;
  }

  if (serviceCategory !== 'TATTOO') {
    return (
      <Typography
        variant="body2"
        color="white"
        style={{ lineHeight: 'normal' }}>
        Other Service
      </Typography>
    );
  }

  return (
    <>
      <Typography
        variant="body2"
        color="white"
        style={{ lineHeight: 'normal' }}>
        {detailsText}
      </Typography>
      <Typography
        variant="body2"
        color="white"
        style={{ lineHeight: 'normal' }}>
        {priceText}
      </Typography>
      <Typography
        variant="body2"
        color="white"
        style={{ lineHeight: 'normal' }}>
        {tattooDetails}
      </Typography>
    </>
  );
};

export const getEmployeeTimezoneOffset = (
  employeeId: string,
  employeesResourceMap?: EmployeesResourceMap,
) => {
  const employeeResource = employeesResourceMap?.[employeeId];
  const employeeTimezoneOffset = employeeResource?.userDbTimezone
    ? getTimezoneOffset(employeeResource?.userDbTimezone) / 60000
    : employeeResource?.timezoneOffset;
  return employeeTimezoneOffset;
};

export const getCalendarEvents = ({
  appointments,
  timeBlocks,
  trips,
  resourceMap,
  sizeOptions,
  employeesToBusiness,
  employeesResourceMap,
}: {
  appointments: Appointment[];
  timeBlocks?: GetBatchTimeBlocksResponse;
  trips?: GetBatchTripsResponse;
  employeesToBusiness: EmployeeToBusiness[];
  resourceMap?: Resource[];
  sizeOptions?: TattooChoices[];
  employeesResourceMap?: EmployeesResourceMap;
}): CalendarEvent[] => {
  const appointmentEvents: CalendarEvent[] =
    appointments?.map(appointment => ({
      type: 'appointment',
      start: parseISO(appointment.startDateTime!),
      end: parseISO(appointment.endDateTime!),
      title: getCustomerNameFromAppointment(appointment),
      description: getAppointmentDisplayText(appointment),
      detailsComponent: getAppointmentDetailsComponent(
        appointment,
        sizeOptions,
      ),
      allDay: false,
      appointmentId: appointment.appointmentId,
      resourceId: appointment.employeeId,
      color:
        resourceMap?.find(
          resource => resource.resourceId === appointment.employeeId,
        )?.color ?? 'blue',
      canceled: appointment.status === 'CANCELED',
    })) || [];

  const timeBlockEvents = (
    timeBlocks
      ? Object.entries(timeBlocks).map(([employeeId, blocks]) => {
          const employee = employeesToBusiness.find(
            e => e.employeeId === employeeId,
          );
          if (!employee) return [];

          const employeeInitials = `${employee.user?.firstName[0] || ''}${
            employee.user?.lastName[0] || ''
          }`;

          const localTimezone =
            Intl.DateTimeFormat().resolvedOptions().timeZone;

          const employeeTimezoneOffset = getEmployeeTimezoneOffset(
            employeeId,
            employeesResourceMap,
          );

          const employeeTimezone = employee.user?.timezone || localTimezone;

          const list = blocks.flatMap(block => {
            const allDates = getAllBlockedDaysFromTimeBlock(block);
            if (!allDates) return [];

            return allDates.map(date => {
              const { start: startDateTime, end: endDateTime } =
                getTimeBlockTimes({
                  timeBlock: block,
                  date,
                  employeeTimezone,
                  employeeTimezoneOffset,
                });

              const isAllDay = block.startTime === 0 && block.endTime === 2400;
              // if (block.reason === 'Lunch') console.log(startDateTime);
              return {
                type: 'time-block',
                timeBlockId: block.id,
                start: parseISO(startDateTime.toISOString()),
                end: parseISO(endDateTime.toISOString()),
                title: `${employeeInitials} - Time Block`,
                description: '',
                allDay: isAllDay,
                color: '#fff',
                textColor: 'rgba(255, 0, 0, .6)',
                resourceId: employeeId,
              } as CalendarEvent;
            });
          });

          return list;
        })
      : []
  ).flat();

  const tripEvents = (
    trips
      ? Object.entries(trips).map(([employeeId, employeeTrips]) => {
          const employee = employeesToBusiness.find(
            e => e.employeeId === employeeId,
          );
          if (!employee) return [];

          const employeeInitials = `${employee.user?.firstName[0] || ''}${
            employee.user?.lastName[0] || ''
          }`;

          const list = employeeTrips.flatMap(trip => {
            const allDates = getAllDaysFromTrip(trip);
            if (!allDates) return [];

            return allDates.map(date => {
              const dateStringWithTime = `${date}T00:00:00`;
              const startDateTime = new Date(dateStringWithTime);
              const endDateTime = new Date(dateStringWithTime);

              startDateTime.setHours(0, 0);
              endDateTime.setHours(23, 59);

              return {
                type: 'trip',
                tripId: trip.tripId,
                start: parseISO(startDateTime.toISOString()),
                end: parseISO(endDateTime.toISOString()),
                title: `${employeeInitials} - Trip`,
                description: '',
                allDay: true,
                color: '#fff',
                textColor: '#CCCC66',
                resourceId: employeeId,
              } as CalendarEvent;
            });
          });

          return list;
        })
      : []
  ).flat();

  return appointmentEvents.concat(timeBlockEvents).concat(tripEvents);
};

export const slotPropGetter = (
  date: Date,
  resourceId: number | string | undefined,
  employeesResourceMap: EmployeesResourceMap = {},
  timeBlocks: GetBatchTimeBlocksResponse = {},
) => {
  if (!resourceId) return {};

  const dayIndex = getDay(date);
  const previousDayIndex = dayIndex === 0 ? 6 : dayIndex - 1;

  const employeeResource = employeesResourceMap[resourceId as string];
  if (!employeeResource) return {};

  const { availability } = employeeResource;
  if (!availability) return {};

  const day = convertIndexToShortDay(dayIndex);
  const previousDay = convertIndexToShortDay(previousDayIndex);

  const dayAvailability = availability[day];
  const previousDayAvailability = availability[previousDay];

  if (!dayAvailability) return {};

  const isOpenAllDay =
    dayAvailability.startTime === 0 && dayAvailability.endTime === 2400;
  if (isOpenAllDay) return {};

  const employeeTimeBlocks = timeBlocks[resourceId as string];
  const isClosedAllDay =
    employeeTimeBlocks &&
    employeeTimeBlocks.find(
      timeBlock =>
        timeBlock.startTime === 0 &&
        timeBlock.endTime === 2400 &&
        dayIndex === getDay(parseISO(timeBlock.startDate)) &&
        isSameDay(parseISO(timeBlock.startDate), date),
    );

  if (isClosedAllDay)
    return {
      style: {
        backgroundColor: '#e0e0e0',
        borderColor: '#e0e0e0',
      },
    };

  const employeeTimezoneOffset = getEmployeeTimezoneOffset(
    resourceId as string,
    employeesResourceMap,
  );

  const defaultTimezoneOffset = getTimezoneOffset(DEFAULT_TIMEZONE) / 60000;

  const timezoneOffset = employeeTimezoneOffset ?? defaultTimezoneOffset;

  const [startClosedTime, endClosedTime] = [
    convertDateToOnschedHours(
      convertOnschedHoursToDateFromOffset(
        dayAvailability.endTime,
        timezoneOffset,
      ),
    ),
    convertDateToOnschedHours(
      convertOnschedHoursToDateFromOffset(
        dayAvailability.startTime,
        timezoneOffset,
      ),
    ),
  ];
  const [previousDayStartClosedTime, previousDayEndClosedTime] = [
    convertDateToOnschedHours(
      convertOnschedHoursToDateFromOffset(
        previousDayAvailability?.endTime ?? 0,
        timezoneOffset,
      ),
    ),
    convertDateToOnschedHours(
      convertOnschedHoursToDateFromOffset(
        previousDayAvailability?.startTime ?? 0,
        timezoneOffset,
      ),
    ),
  ];

  const dateOnschedHours = convertDateToOnschedHours(date);
  const goesToNextDay = startClosedTime < endClosedTime;

  const previousDayCarriesOver =
    !!previousDayAvailability &&
    previousDayStartClosedTime < previousDayEndClosedTime;

  const isOpenFromPreviousDay =
    previousDayCarriesOver && dateOnschedHours < previousDayStartClosedTime;

  const isCurrentTimeClosed =
    !isOpenFromPreviousDay &&
    (startClosedTime === endClosedTime ||
      (goesToNextDay
        ? // only include slots in current day
          dateOnschedHours < endClosedTime
        : dateOnschedHours >= startClosedTime ||
          dateOnschedHours < endClosedTime));

  if (!isCurrentTimeClosed) return {};

  return {
    style: {
      backgroundColor: '#e0e0e0',
      borderColor: '#e0e0e0',
    },
  };
};

export const getEarliestAvailabilities = (
  employeesResourceMap: EmployeesResourceMap,
  selectedArtists?: string[],
) => {
  const earliestAvailabilities: Record<string, Date> = {};
  Object.entries(employeesResourceMap).forEach(
    ([employeeId, employeeResource]) => {
      if (selectedArtists && !selectedArtists.includes(employeeId)) return;

      const { availability, timezoneOffset, userDbTimezone } = employeeResource;
      if (!availability) return;

      const newTimezoneOffset = userDbTimezone
        ? getTimezoneOffset(userDbTimezone) / 60000
        : timezoneOffset;

      Object.entries(availability).forEach(([day, dayAvailability]) => {
        if (!dayAvailability) return;

        const { startTime = 0, endTime = 0 } =
          dayAvailability as OnSchedCommonType.Time;

        const isDayClosed = startTime === 0 && endTime === 0;
        if (isDayClosed) return;

        const startTimeDate = convertOnschedHoursToDateFromOffset(
          startTime,
          newTimezoneOffset,
        );

        const currentEarliest = earliestAvailabilities[day];

        if (
          !currentEarliest ||
          startTimeDate.getTime() < currentEarliest.getTime()
        ) {
          earliestAvailabilities[day] = startTimeDate;
        }
      });
    },
  );

  return earliestAvailabilities;
};
